// eslint-disable no-param-reassign
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FileUploader } from '@brighterion/br-ui';
import XLSX from 'xlsx';
import _ from 'lodash';

import styles from './index.css';
import RulesBulkUpload from '../RulesBulkUpload';

type Props = {
    rulesTypeText: string;
    rulesType: RulesType;
    uploaderConfig: Record<string, any>;
    setParsedFileData: (fileData: any) => void;
    uploadedFile?: File;
    setUploadedFile: (file?: File) => void;
    specificUploadInstructions?: string;
};

export default function Uploader({
    rulesTypeText,
    rulesType,
    uploaderConfig,
    setParsedFileData,
    uploadedFile,
    setUploadedFile,
    specificUploadInstructions
}: Props) {
    const { t } = useTranslation();
    const fileUploaderRef = useRef(null);

    const castCell = (accessor: string, cell: any) => {
        switch (accessor) {
            case 'Score Value':
            case 'threshold':
                return parseInt(cell, 10);
            default:
                return cell;
        }
    };
    function parseAndValidateFile(file: any) {
        return new Promise((resolve) => {
            const reader = new FileReader();
            const binaryReader = !!reader.readAsBinaryString;
            let missingRequiredColumns = [];
            let currErr: string;

            reader.onload = (e) => {
                const binaryString = e.target && e.target.result ? e.target.result : {};
                const workBook = XLSX.read(binaryString, {
                    type: binaryReader ? 'binary' : 'array',
                    bookVBA: true,
                    raw: true
                });
                const workSheetName = workBook.SheetNames[0];
                const workSheet = workBook.Sheets[workSheetName];
                // TODO: fix the typings here
                const requiredColumnAccessors = uploaderConfig.columns.reduce((acc: any, currentColumn: any) => {
                    if (currentColumn.required) acc.push(currentColumn.accessor);
                    return acc;
                }, []);
                let data: any[] = XLSX.utils.sheet_to_json(workSheet);
                const parsedFileColumns: any = XLSX.utils.sheet_to_json(workSheet, { header: 1 })[0];
                // map acceptable header used (if any) to the concept it represents
                const matchedColumnsMap = parsedFileColumns.reduce(
                    (acc: Record<string, any>, currentColumn: string) => {
                        const trimmedLowerCurrentColumn = currentColumn.toString().trim().toLowerCase();
                        const reqColumnObject = uploaderConfig.columns.find((reqCol: any) => {
                            const isInAcceptableHeaders = reqCol?.acceptableHeaders.some(
                                (acceptableHeader: string) =>
                                    acceptableHeader.toLowerCase() === trimmedLowerCurrentColumn
                            );
                            return isInAcceptableHeaders;
                        });
                        if (reqColumnObject) acc[reqColumnObject.accessor] = currentColumn;
                        return acc;
                    },
                    {}
                );
                const matchedColumns = Object.keys(matchedColumnsMap);
                missingRequiredColumns = _.difference(requiredColumnAccessors, matchedColumns);
                if (!(missingRequiredColumns.length === 0)) {
                    currErr = `Missing Required Columns: ${missingRequiredColumns.toString()}`;
                } else if (matchedColumns.length <= requiredColumnAccessors.length) {
                    currErr = 'Must have at least one optional column';
                } else {
                    // final columns and data to be passed to react-table
                    const columns = uploaderConfig.columns.map((originalCol: any) => ({
                        accessor: matchedColumnsMap[originalCol.accessor],
                        Header: originalCol.Header
                    }));
                    data = data.map((dataRow) => {
                        const processedDataRow: any = {};
                        matchedColumns.forEach((columnAccessor: string) => {
                            const parsedColumnHeader = matchedColumnsMap[columnAccessor];
                            if (dataRow[parsedColumnHeader] || dataRow[parsedColumnHeader] === 0)
                                processedDataRow[parsedColumnHeader] = castCell(
                                    parsedColumnHeader,
                                    dataRow[parsedColumnHeader]
                                );
                        });

                        return processedDataRow;
                    });
                    setParsedFileData({
                        columns,
                        data,
                        matchedColumnsMap
                    });
                }
                resolve({ errorMsg: currErr });
            };
            if (binaryReader) {
                reader.readAsBinaryString(file);
            } else {
                reader.readAsArrayBuffer(file);
            }
        });
    }

    const parse = async (file: File) => {
        const validationResult = await parseAndValidateFile(file);
        return validationResult;
    };

    if (!uploaderConfig) return null;

    const { maxSize, fileTypes } = uploaderConfig;
    return (
        <div>
            <RulesBulkUpload.Title title={t('Upload Wizard Title', { rulesTypeText })} dataTestId='upload-page-title' />
            <RulesBulkUpload.UploadInstructions
                dataTestId='upload-page-instructions'
                instructions={
                    <>
                        {specificUploadInstructions || t('upload general instructions', { rulesTypeText })}{' '}
                        {t('upload file size instructions', { fileSize: `${maxSize / 1000000} MB`, fileTypes })}{' '}
                        {t('bulk upload single-threshold limitation')}
                    </>
                }
            />
            <FileUploader
                dataTestId={`${rulesType}-uploader`}
                ref={fileUploaderRef}
                accept={fileTypes}
                className={styles.uploaderContainer}
                onChangeProp={(files: File[]) => {
                    setUploadedFile(files[0]);
                }}
                disabled={false}
                onDelete={() => {
                    setUploadedFile(undefined);
                    setParsedFileData(undefined);
                }}
                maxSize={maxSize}
                maxTotalSize={maxSize}
                initialFile={uploadedFile ? [uploadedFile] : undefined}
                name='file'
                placeholder={
                    <>
                        {`${t('Drag and drop file here or')} `}
                        <span className={styles.browse}>{t('Browse')}</span>
                    </>
                }
                successMsg={t('File is ready to preview and upload')}
                // FIXME: Uploader can't take an async function in
                // @ts-ignore
                validationFunction={parse}
            />
        </div>
    );
}
